import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import ContactsImport from "./ContactsImport";
import CampaignComposer from "./CampaignComposer";
import WhatsAppConnect from "./WhatsAppConnect";
import CrmBuyers from "./CrmBuyers";
import { Upload, Send, Trash2, Search, Users, History, CheckCircle2, XCircle, Clock, Ban, Smartphone, Home, Tag as TagIcon } from "lucide-react";

const STATUS_TAGS = ["Vuole vendere", "Venduto", "Venderà", "Vuole Affittare"];

const STATUS_LABEL = {
    sent: ["Inviato", "text-green-300"], failed: ["Fallito", "text-red-300"], pending: ["In attesa", "text-amber-300"],
    cancelled: ["Interrotto", "text-ink-400"], skipped_no_consent: ["No consenso", "text-orange-300"],
    skipped_invalid: ["Dato mancante", "text-orange-300"], skipped_unsubscribed: ["Disiscritto", "text-orange-300"],
};

export default function CrmContacts() {
    const [data, setData] = useState({ items: [], facets: { tags: [], cities: [], batches: [] }, total: 0 });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ tag: "", city: "", q: "", wants_to_sell: false });
    const [selected, setSelected] = useState(new Set());
    const [showImport, setShowImport] = useState(false);
    const [composer, setComposer] = useState(null);
    const [view, setView] = useState("contacts");
    const [campaigns, setCampaigns] = useState(null);
    const [campDetail, setCampDetail] = useState(null);
    const [showWaConnect, setShowWaConnect] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { q: filters.q || undefined, tag: filters.tag || undefined, city: filters.city || undefined, wants_to_sell: filters.wants_to_sell ? "true" : undefined };
            const r = await api.get("/crm/contacts", { params });
            setData(r.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const loadCampaigns = async () => {
        try { const r = await api.get("/crm/campaigns"); setCampaigns(r.data.items || []); } catch (e) { console.error(e); }
    };
    useEffect(() => { if (view === "history") loadCampaigns(); }, [view]);

    const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => setSelected((s) => s.size === data.items.length ? new Set() : new Set(data.items.map((c) => c.id)));

    const deleteSelected = async () => {
        if (!window.confirm(`Eliminare ${selected.size} contatti?`)) return;
        await api.post("/crm/contacts/bulk-delete", { ids: [...selected] });
        setSelected(new Set());
        load();
    };

    const openComposer = () => {
        const contacts = data.items.filter((c) => selected.has(c.id));
        if (contacts.length === 0) return alert("Seleziona almeno un contatto");
        setComposer({ contacts });
    };

    const selectSellers = () => {
        setFilters((f) => ({ ...f, wants_to_sell: true, tag: "" }));
        setTimeout(() => setSelected(new Set(data.items.filter((c) => c.wants_to_sell).map((c) => c.id))), 0);
    };

    const selectByStatusTag = (tagName) => {
        setFilters((f) => ({ ...f, wants_to_sell: false, tag: tagName }));
        setTimeout(() => setSelected(new Set(data.items.filter((c) => (c.tags || []).includes(tagName)).map((c) => c.id))), 0);
    };

    const openCampDetail = async (id) => {
        try { const r = await api.get(`/crm/campaigns/${id}`); setCampDetail(r.data); } catch (e) { console.error(e); }
    };

    return (
        <div data-testid="crm-contacts-tab">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h3 className="font-serif text-2xl text-ink-50 flex items-center gap-2"><Users size={20} className="text-gold" /> Contatti &amp; Comunicazioni</h3>
                    <p className="text-xs text-ink-400 mt-1">{data.total} contatti in rubrica · importa da Excel/CSV e invia comunicazioni massive Email/WhatsApp</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button data-testid="wa-connect-open" onClick={() => setShowWaConnect(true)} className="cta-outline px-4 py-2 text-xs flex items-center gap-2"><Smartphone size={13} /> WhatsApp</button>
                    <button data-testid="contacts-view-toggle" onClick={() => setView(view === "contacts" ? "history" : "contacts")} className="cta-outline px-4 py-2 text-xs flex items-center gap-2">
                        <History size={13} /> {view === "contacts" ? "Storico invii" : "Torna ai contatti"}
                    </button>
                    <button data-testid="contacts-import-btn" onClick={() => setShowImport(true)} className="cta-gold px-4 py-2 text-xs flex items-center gap-2"><Upload size={13} /> Importa Excel/CSV</button>
                </div>
            </div>

            {view !== "history" && (
                <div className="flex gap-1 mb-5 border-b border-white/10">
                    <button data-testid="audience-owners" onClick={() => setView("contacts")}
                        className={`px-4 py-2 text-xs flex items-center gap-2 border-b-2 -mb-px ${view === "contacts" ? "border-gold text-gold" : "border-transparent text-ink-400 hover:text-ink-100"}`}>
                        <Users size={13} /> Proprietari / Rubrica
                    </button>
                    <button data-testid="audience-buyers" onClick={() => setView("buyers")}
                        className={`px-4 py-2 text-xs flex items-center gap-2 border-b-2 -mb-px ${view === "buyers" ? "border-gold text-gold" : "border-transparent text-ink-400 hover:text-ink-100"}`}>
                        <Home size={13} /> Acquirenti (matching)
                    </button>
                </div>
            )}

            {view === "history" ? (
                <CampaignHistory campaigns={campaigns} onOpen={openCampDetail} />
            ) : view === "buyers" ? (
                <CrmBuyers onCompose={(buyers) => setComposer({ buyers })} />
            ) : (
                <>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <div className="relative">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                            <input data-testid="contacts-search" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Cerca nome, email, telefono..."
                                className="bg-transparent border border-white/15 pl-8 pr-3 py-2 text-xs text-ink-50 focus:border-gold outline-none w-56" />
                        </div>
                        <select data-testid="contacts-filter-tag" value={filters.tag} onChange={(e) => setFilters((f) => ({ ...f, tag: e.target.value }))} className="bg-ink-950 border border-white/15 px-3 py-2 text-xs text-ink-100 outline-none">
                            <option value="">Tutti i tag</option>
                            {(data.facets?.tags || []).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select data-testid="contacts-filter-city" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} className="bg-ink-950 border border-white/15 px-3 py-2 text-xs text-ink-100 outline-none">
                            <option value="">Tutte le città</option>
                            {(data.facets?.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button data-testid="contacts-filter-sellers" onClick={selectSellers}
                            className={`px-3 py-2 text-xs border transition-colors ${filters.wants_to_sell ? "border-gold text-gold bg-gold/10" : "border-white/15 text-ink-300 hover:text-gold"}`}>
                            🏠 Vuole vendere
                        </button>
                        <button data-testid="contacts-filter-sold" onClick={() => selectByStatusTag("Venduto")}
                            className={`px-3 py-2 text-xs border transition-colors ${filters.tag === "Venduto" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-ink-300 hover:text-gold"}`}>
                            ✓ Venduto
                        </button>
                        <button data-testid="contacts-filter-will-sell" onClick={() => selectByStatusTag("Venderà")}
                            className={`px-3 py-2 text-xs border transition-colors ${filters.tag === "Venderà" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-ink-300 hover:text-gold"}`}>
                            ⏳ Venderà
                        </button>
                        <button data-testid="contacts-filter-wants-rent" onClick={() => selectByStatusTag("Vuole Affittare")}
                            className={`px-3 py-2 text-xs border transition-colors ${filters.tag === "Vuole Affittare" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-ink-300 hover:text-gold"}`}>
                            🔑 Vuole Affittare
                        </button>
                        {(filters.wants_to_sell || ["Venduto", "Venderà", "Vuole Affittare"].includes(filters.tag)) && <button onClick={() => setFilters((f) => ({ ...f, wants_to_sell: false, tag: "" }))} className="text-xs text-ink-500 hover:text-ink-200">✕ reset</button>}
                    </div>

                    {selected.size > 0 && (
                        <div data-testid="contacts-selection-bar" className="flex items-center gap-3 mb-4 p-3 border border-gold/40 bg-gold/5 text-xs">
                            <span className="text-gold font-medium">{selected.size} selezionati</span>
                            <button data-testid="contacts-send-btn" onClick={openComposer} className="cta-gold px-4 py-1.5 flex items-center gap-2"><Send size={12} /> Invia comunicazione</button>
                            <button data-testid="contacts-delete-btn" onClick={deleteSelected} className="px-3 py-1.5 border border-red-700/40 text-red-300 hover:bg-red-900/20 flex items-center gap-2"><Trash2 size={12} /> Elimina</button>
                        </div>
                    )}

                    {loading ? <div className="p-10 text-center text-ink-400 text-sm">Caricamento contatti...</div> : (
                        <div className="border border-white/10 overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-ink-500 border-b border-white/10">
                                        <th className="p-3 w-8"><input type="checkbox" data-testid="contacts-select-all" checked={data.items.length > 0 && selected.size === data.items.length} onChange={toggleAll} /></th>
                                        <th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Telefono</th>
                                        <th className="p-3">Città</th><th className="p-3">Immobile</th><th className="p-3">Tag</th><th className="p-3">Consenso</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-ink-500">Nessun contatto. Importa il tuo primo file Excel/CSV per iniziare.</td></tr>}
                                    {data.items.map((c) => (
                                        <tr key={c.id} data-testid={`contact-row-${c.id}`} className={`border-b border-white/5 hover:bg-white/[0.03] ${c.unsubscribed ? "opacity-50" : ""}`}>
                                            <td className="p-3"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} /></td>
                                            <td className="p-3 text-ink-50 font-medium">{c.first_name} {c.last_name}{c.unsubscribed && <span className="ml-1 text-[9px] text-red-300">(disiscritto)</span>}</td>
                                            <td className="p-3 text-ink-300">{c.email || "—"}</td>
                                            <td className="p-3 text-ink-300 font-mono">{c.phone || "—"}</td>
                                            <td className="p-3 text-ink-300">{c.city || "—"}</td>
                                            <td className="p-3 text-ink-400 max-w-[180px] truncate">{c.property_address || "—"}</td>
                                            <td className="p-3"><InlineTagEditor contact={c} onChange={load} /></td>
                                            <td className="p-3">{c.marketing_consent ? <CheckCircle2 size={13} className="text-green-400" /> : <Ban size={13} className="text-orange-400" title="Nessun consenso marketing: escluso dagli invii" />}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {showImport && <ContactsImport onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
            {composer && <CampaignComposer contacts={composer.contacts} buyers={composer.buyers} onClose={() => setComposer(null)} onDone={async () => { setComposer(null); await loadCampaigns(); setView("history"); }} />}
            {campDetail && <CampaignDetailModal camp={campDetail} onClose={() => setCampDetail(null)} />}
            {showWaConnect && <WhatsAppConnect onClose={() => setShowWaConnect(false)} />}
        </div>
    );
}

function CampaignHistory({ campaigns, onOpen }) {
    if (campaigns === null) return <div className="p-8 text-center text-ink-400 text-sm border border-white/10">Caricamento storico...</div>;
    return (
        <div data-testid="campaigns-history" className="space-y-3">
            {campaigns.length === 0 && <div className="p-8 text-center text-ink-500 text-sm border border-white/10">Nessun invio effettuato finora.</div>}
            {campaigns.map((c) => (
                <button key={c.id} data-testid={`campaign-${c.id}`} onClick={() => onOpen(c.id)} className="w-full text-left p-4 border border-white/10 hover:border-gold/40 bg-ink-900/40 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border mr-2 ${c.channel === "email" ? "border-blue-700/40 text-blue-300" : "border-green-700/40 text-green-300"}`}>
                            {c.channel === "email" ? "✉ Email" : "💬 WhatsApp"}{c.whatsapp_mode === "semi_manual" && " · semi-manuale"}{c.whatsapp_mode === "automatic" && " · automatica"}
                        </span>
                        {c.audience === "buyers" && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 border border-gold/40 text-gold mr-2">Acquirenti</span>}
                        <span className="text-sm text-ink-50">{c.subject || "Campagna WhatsApp"}</span>
                        <div className="text-[10px] text-ink-500 mt-1">{new Date(c.created_at).toLocaleString("it-IT")}</div>
                    </div>
                    <div className="flex gap-3 text-[11px]">
                        <span className="text-green-300 flex items-center gap-1"><CheckCircle2 size={11} /> {c.stats.sent}</span>
                        {c.stats.failed > 0 && <span className="text-red-300 flex items-center gap-1"><XCircle size={11} /> {c.stats.failed}</span>}
                        {c.stats.pending > 0 && <span className="text-amber-300 flex items-center gap-1"><Clock size={11} /> {c.stats.pending}</span>}
                        {c.stats.skipped > 0 && <span className="text-orange-300 flex items-center gap-1"><Ban size={11} /> {c.stats.skipped}</span>}
                        <span className="text-ink-500">/ {c.stats.total}</span>
                    </div>
                </button>
            ))}
        </div>
    );
}

function CampaignDetailModal({ camp, onClose }) {
    return (
        <div className="fixed inset-0 z-[10060] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
            <div data-testid="campaign-detail-modal" className="bg-ink-950 border border-white/15 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-serif text-xl text-ink-50">{camp.subject || "Campagna WhatsApp"}</h4>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-50">✕</button>
                </div>
                <div className="text-xs text-ink-400 mb-4 whitespace-pre-wrap border border-white/10 p-3 bg-ink-900/40 max-h-32 overflow-y-auto">{camp.body}</div>
                <table className="w-full text-xs">
                    <thead><tr className="text-left text-[10px] uppercase text-ink-500 border-b border-white/10"><th className="p-2">Contatto</th><th className="p-2">Stato</th><th className="p-2">Dettaglio</th></tr></thead>
                    <tbody>
                        {camp.recipients.map((r) => {
                            const [label, color] = STATUS_LABEL[r.status] || [r.status, "text-ink-400"];
                            return (
                                <tr key={r.contact_id} className="border-b border-white/5">
                                    <td className="p-2 text-ink-100">{r.name}</td>
                                    <td className={`p-2 ${color}`}>{label}</td>
                                    <td className="p-2 text-ink-500">{r.error || r.email || r.phone || ""}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function InlineTagEditor({ contact, onChange }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tags, setTags] = useState(contact.tags || []);
    const [wantsToSell, setWantsToSell] = useState(!!contact.wants_to_sell);
    const wrapRef = useRef(null);

    // Sync when contact changes
    useEffect(() => {
        setTags(contact.tags || []);
        setWantsToSell(!!contact.wants_to_sell);
    }, [contact.tags, contact.wants_to_sell, contact.id]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    const isActive = (t) => (t === "Vuole vendere" ? wantsToSell : tags.includes(t));

    const persist = async (nextTags, nextWantsToSell) => {
        setSaving(true);
        try {
            await api.put(`/crm/contacts/${contact.id}`, { tags: nextTags, wants_to_sell: nextWantsToSell });
            setTags(nextTags);
            setWantsToSell(nextWantsToSell);
            if (onChange) onChange();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Errore aggiornamento tag");
        } finally {
            setSaving(false);
        }
    };

    const toggle = (t) => {
        if (saving) return;
        if (t === "Vuole vendere") {
            const nextWts = !wantsToSell;
            let nextTags = [...tags];
            const autoTag = "Proprietario - vuole vendere";
            if (nextWts && !nextTags.includes(autoTag)) nextTags = [...nextTags, autoTag];
            if (!nextWts) nextTags = nextTags.filter((x) => x !== autoTag);
            persist(nextTags, nextWts);
        } else {
            const nextTags = tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t];
            persist(nextTags, wantsToSell);
        }
    };

    return (
        <div ref={wrapRef} className="relative flex flex-wrap items-center gap-1">
            {(tags || []).map((t) => (
                <span key={t} className="inline-block px-1.5 py-0.5 text-[9px] border border-gold/30 text-gold bg-gold/5">{t}</span>
            ))}
            <button
                data-testid={`inline-tag-btn-${contact.id}`}
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                className={`ml-1 p-1 border transition-colors ${open ? "border-gold text-gold bg-gold/10" : "border-white/15 text-ink-400 hover:text-gold hover:border-gold/50"}`}
                title="Assegna tag rapido"
            >
                <TagIcon size={10} />
            </button>
            {open && (
                <div
                    data-testid={`inline-tag-popover-${contact.id}`}
                    className="absolute left-0 top-full mt-1 z-30 bg-ink-950 border border-gold/40 p-2 shadow-xl min-w-[200px]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="text-[9px] uppercase tracking-wider text-ink-500 px-1 pb-1.5 mb-1 border-b border-white/10">Stato contatto</div>
                    <div className="flex flex-wrap gap-1">
                        {STATUS_TAGS.map((t) => {
                            const active = isActive(t);
                            return (
                                <button
                                    key={t}
                                    data-testid={`inline-tag-toggle-${contact.id}-${t}`}
                                    onClick={() => toggle(t)}
                                    disabled={saving}
                                    className={`px-2 py-1 text-[10px] border transition-colors ${active ? "border-gold text-gold bg-gold/10" : "border-white/15 text-ink-300 hover:text-gold hover:border-gold/50"} ${saving ? "opacity-50 cursor-wait" : ""}`}
                                >
                                    {active && <span className="mr-0.5">✓</span>}{t}
                                </button>
                            );
                        })}
                    </div>
                    {saving && <div className="text-[9px] text-ink-500 mt-2 px-1">Salvataggio...</div>}
                </div>
            )}
        </div>
    );
}
